import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ActionItem, AsyncState, Newsletter } from '@/types';
import * as newsletterService from '@/services/newsletterService';
import { carryForwardIncompleteItems } from '@/services/actionItemService';
import { snapshotNewsletter } from '@/services/versionService';

interface NewslettersState extends AsyncState {
  items: Newsletter[];
}

const initialState: NewslettersState = {
  items: [],
  status: 'idle',
  error: null,
};

export const fetchNewsletters = createAsyncThunk('newsletters/fetchAll', async () => {
  return newsletterService.listNewsletters();
});

export const createNewsletterWithCarryForward = createAsyncThunk<
  { newsletter: Newsletter; carriedItems: ActionItem[] },
  void,
  { state: { newsletters: NewslettersState } }
>('newsletters/createWithCarryForward', async (_, { getState }) => {
  const latest = getState().newsletters.items[0];
  const newsletter = await newsletterService.createDraftNewsletter();
  const carriedItems = latest ? await carryForwardIncompleteItems(latest.id, newsletter.id) : [];
  return { newsletter, carriedItems };
});

export const saveNewsletterThunk = createAsyncThunk('newsletters/save', async (newsletter: Newsletter) => {
  return newsletterService.saveNewsletter(newsletter);
});

export const publishNewsletterThunk = createAsyncThunk(
  'newsletters/publish',
  async ({ newsletter, actionItemIds, note }: { newsletter: Newsletter; actionItemIds: string[]; note?: string }) => {
    const published = await newsletterService.publishNewsletter(newsletter);
    const version = await snapshotNewsletter(published, actionItemIds, note ?? 'Published');
    return { newsletter: { ...published, currentVersion: version.versionNumber }, version };
  },
);

export const deleteNewsletterThunk = createAsyncThunk('newsletters/delete', async (id: string) => {
  await newsletterService.deleteNewsletter(id);
  return id;
});

const newslettersSlice = createSlice({
  name: 'newsletters',
  initialState,
  reducers: {
    upsertNewsletterLocal(state, action: PayloadAction<Newsletter>) {
      const index = state.items.findIndex((n) => n.id === action.payload.id);
      if (index === -1) state.items.unshift(action.payload);
      else state.items[index] = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewsletters.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchNewsletters.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchNewsletters.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load newsletters';
      })
      .addCase(createNewsletterWithCarryForward.fulfilled, (state, action) => {
        state.items.unshift(action.payload.newsletter);
        state.items.sort((a, b) => b.issueNumber - a.issueNumber);
      })
      .addCase(saveNewsletterThunk.fulfilled, (state, action) => {
        const index = state.items.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(publishNewsletterThunk.fulfilled, (state, action) => {
        const index = state.items.findIndex((n) => n.id === action.payload.newsletter.id);
        if (index !== -1) state.items[index] = action.payload.newsletter;
      })
      .addCase(deleteNewsletterThunk.fulfilled, (state, action) => {
        state.items = state.items.filter((n) => n.id !== action.payload);
      });
  },
});

export const { upsertNewsletterLocal } = newslettersSlice.actions;
export default newslettersSlice.reducer;
